import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, X } from "lucide-react";

interface OnboardingChecklistProps {
  shopId: string | null | undefined;
  /** From dashboard stats: number of products with a ready AR asset. */
  productsWithAr: number;
  /** From dashboard stats: total AR views on the storefront. */
  totalArViews: number;
  /** Opens the theme editor deep link (App Bridge top-level redirect). */
  onOpenThemeEditor: () => void;
}

interface StepDef {
  key: string;
  title: string;
  description: string;
  done: boolean;
  actionLabel: string;
  action: () => void;
}

/**
 * Light coaching card for new merchants: three steps from install to a live
 * AR experience. Auto-checks steps off using existing dashboard stats and is
 * dismissible (persisted per shop in localStorage).
 */
export function OnboardingChecklist({
  shopId,
  productsWithAr,
  totalArViews,
  onOpenThemeEditor,
}: OnboardingChecklistProps) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(true);
  const [themeStepDone, setThemeStepDone] = useState(false);

  const dismissKey = shopId ? `ar-onboarding-dismissed:${shopId}` : null;
  const themeKey = shopId ? `ar-onboarding-theme-opened:${shopId}` : null;

  useEffect(() => {
    if (!dismissKey) return;
    try {
      setDismissed(localStorage.getItem(dismissKey) === "1");
      setThemeStepDone(themeKey ? localStorage.getItem(themeKey) === "1" : false);
    } catch {
      setDismissed(false);
    }
  }, [dismissKey, themeKey]);

  const dismiss = () => {
    setDismissed(true);
    if (dismissKey) {
      try {
        localStorage.setItem(dismissKey, "1");
      } catch {
        // Storage blocked — card will reappear next load; harmless.
      }
    }
  };

  const steps: StepDef[] = [
    {
      key: "generate",
      title: "Generate your first 3D model",
      description: "Pick a product and turn its photo into an AR-ready model.",
      done: productsWithAr > 0,
      actionLabel: "Go to products",
      action: () => navigate("/products"),
    },
    {
      key: "theme",
      title: "Enable the AR viewer block",
      description: "Add the AR Space Viewer block to your product pages — one click in the theme editor.",
      // No API tells us whether the block is live, so we check this off once
      // the merchant has opened the theme editor from here.
      done: themeStepDone,
      actionLabel: "Open theme editor",
      action: () => {
        if (themeKey) {
          try {
            localStorage.setItem(themeKey, "1");
          } catch {
            // ignore
          }
        }
        setThemeStepDone(true);
        onOpenThemeEditor();
      },
    },
    {
      key: "view",
      title: "View AR on your storefront",
      description: "Open a product page on your store and tap \u201cView in AR\u201d.",
      done: totalArViews > 0,
      actionLabel: "How it works",
      action: () => navigate("/products?has_asset=true"),
    },
  ];

  const completedCount = steps.filter((s) => s.done).length;
  const allDone = completedCount === steps.length;

  if (dismissed || allDone) {
    return null;
  }

  return (
    <div className="glass-panel border-indigo-100">
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="ghost-chip text-indigo-600">Getting started</span>
          <h3 className="mt-3 text-xl font-semibold text-slate-900">
            Set up AR in 3 steps
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {completedCount} of {steps.length} complete
          </p>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss checklist"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4 space-y-3">
        {steps.map((step, index) => (
          <div
            key={step.key}
            className={`flex flex-col gap-3 rounded-2xl border p-4 md:flex-row md:items-center md:justify-between ${
              step.done
                ? "border-emerald-100 bg-emerald-50/50"
                : "border-slate-100 bg-white/60"
            }`}
          >
            <div className="flex items-start gap-3">
              {step.done ? (
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
              ) : (
                <Circle className="mt-0.5 h-5 w-5 shrink-0 text-slate-300" />
              )}
              <div>
                <p className={`text-sm font-semibold ${step.done ? "text-emerald-800" : "text-slate-900"}`}>
                  {index + 1}. {step.title}
                </p>
                <p className={`mt-0.5 text-xs ${step.done ? "text-emerald-600" : "text-slate-500"}`}>
                  {step.description}
                </p>
              </div>
            </div>
            {!step.done && (
              <button
                onClick={step.action}
                className="w-full shrink-0 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100 md:w-auto"
              >
                {step.actionLabel}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
